/**
 * Status, priority, category, and view constants for the support workspace.
 *
 * Centralized here so the inbox, detail page, filter sidebar, and status
 * pills all read from a single source. A future AI drafter agent will also
 * read these values for classification.
 */

import {
  Bug,
  Lightbulb,
  MessageSquare,
  User,
  CreditCard,
  FileText,
  Inbox,
  UserCheck,
  Sparkles,
  Clock,
  PauseCircle,
  CheckCircle2,
  UserX,
  Folder,
  type LucideIcon,
} from 'lucide-react';

export type TicketStatus =
  | 'new'
  | 'open'
  | 'pending'
  | 'on_hold'
  | 'solved'
  | 'closed';

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export type TicketSource = 'web' | 'app' | 'email';

export const STATUS_ORDER: TicketStatus[] = [
  'new',
  'open',
  'pending',
  'on_hold',
  'solved',
  'closed',
];

export const PRIORITY_ORDER: TicketPriority[] = ['urgent', 'high', 'normal', 'low'];

export const STATUS_LABELS: Record<TicketStatus, string> = {
  new: 'New',
  open: 'Open',
  pending: 'Pending',
  on_hold: 'On hold',
  solved: 'Solved',
  closed: 'Closed',
};

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

// Mission 10 tokenized (Phase 2.1). Values resolve to the CSS-var token layer
// in index.css; consumers (pills, rows, filters) apply these as CSS colors.
export const STATUS_COLORS: Record<TicketStatus, { bg: string; text: string; dot: string }> = {
  new: { bg: 'var(--status-info-bg)', text: 'var(--status-info-text)', dot: 'var(--status-info)' },
  open: { bg: 'var(--status-cyan-bg)', text: 'var(--status-cyan-text)', dot: 'var(--status-cyan)' },
  pending: { bg: 'var(--status-warning-bg)', text: 'var(--status-warning-text)', dot: 'var(--status-warning)' },
  on_hold: { bg: 'var(--status-purple-bg)', text: 'var(--status-purple-text)', dot: 'var(--status-purple)' },
  solved: { bg: 'var(--status-success-bg)', text: 'var(--status-success-text)', dot: 'var(--status-success)' },
  closed: { bg: 'var(--surface-muted)', text: 'var(--text-strong)', dot: 'var(--text-secondary)' },
};

export const PRIORITY_COLORS: Record<TicketPriority, { bg: string; text: string; dot: string }> = {
  low: { bg: 'var(--surface-muted)', text: 'var(--text-strong)', dot: 'var(--text-tertiary)' },
  normal: { bg: 'var(--status-indigo-bg)', text: 'var(--status-indigo-text)', dot: 'var(--status-indigo)' },
  high: { bg: 'var(--status-orange-bg)', text: 'var(--status-orange-text)', dot: 'var(--status-orange)' },
  urgent: { bg: 'var(--status-error-bg)', text: 'var(--status-error-text)', dot: 'var(--status-error)' },
};

// Icons are lucide components (Mission 10: no emoji chrome). Consumers render
// <entry.icon size={…} />.
export const CATEGORY_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  bug: { label: 'Bug', icon: Bug },
  problem: { label: 'Bug', icon: Bug },
  Bug: { label: 'Bug', icon: Bug },
  feature_request: { label: 'Feature request', icon: Lightbulb },
  feature_idea: { label: 'Feature request', icon: Lightbulb },
  'Feature Request': { label: 'Feature request', icon: Lightbulb },
  feedback: { label: 'Feedback', icon: MessageSquare },
  'Account Issue': { label: 'Account', icon: User },
  account: { label: 'Account', icon: User },
  Billing: { label: 'Billing', icon: CreditCard },
  billing: { label: 'Billing', icon: CreditCard },
  Other: { label: 'Other', icon: FileText },
  other: { label: 'Other', icon: FileText },
};

export const SOURCE_LABELS: Record<TicketSource, { label: string; bg: string; text: string }> = {
  web: { label: 'Web', bg: 'var(--status-success-bg)', text: 'var(--status-success-text)' },
  app: { label: 'App', bg: 'var(--status-info-bg)', text: 'var(--status-info-text)' },
  email: { label: 'Email', bg: 'var(--status-purple-bg)', text: 'var(--status-purple-text)' },
};

export type TicketView =
  | 'inbox'
  | 'mine'
  | 'new'
  | 'pending'
  | 'on_hold'
  | 'solved'
  | 'unassigned'
  | 'all';

export const VIEW_LABELS: Record<TicketView, { label: string; icon: LucideIcon; description: string }> = {
  inbox: { label: 'Inbox', icon: Inbox, description: 'All open tickets' },
  mine: { label: 'Assigned to me', icon: UserCheck, description: 'Your tickets' },
  new: { label: 'New', icon: Sparkles, description: 'Unseen tickets' },
  pending: { label: 'Waiting on reply', icon: Clock, description: 'Awaiting requester' },
  on_hold: { label: 'On hold', icon: PauseCircle, description: 'Blocked internally' },
  solved: { label: 'Solved', icon: CheckCircle2, description: 'Recently solved' },
  unassigned: { label: 'Unassigned', icon: UserX, description: 'No owner yet' },
  all: { label: 'All tickets', icon: Folder, description: 'Everything' },
};

// Brand palette → Mission 10 CSS-var tokens (index.css). Admin is web-only;
// consumers apply these as CSS color values. (`gold` removed — it was unused.)
export const BRAND = {
  green: 'var(--cedar-primary)',
  greenDark: 'var(--cedar-primary)',
  text: 'var(--text-primary)',
  textSecondary: 'var(--text-strong)',
  textTertiary: 'var(--text-secondary)',
  textMuted: 'var(--text-tertiary)',
  surface: 'var(--surface)',
  surfaceAlt: 'var(--surface-subtle)',
  surfaceMuted: 'var(--surface-muted)',
  border: 'var(--border)',
  borderSoft: 'var(--surface-muted)',
  greenSoft: 'var(--surface-green-soft)',
  noteYellow: 'var(--note-bg)',
  noteYellowBorder: 'var(--note-border)',
};
